import { useEffect, useState } from 'react';

import { useGoogleOneTapLogin } from '@react-oauth/google';
import jwtDecode from 'jwt-decode';
import { useCookies } from 'react-cookie';
import { useHistory } from 'react-router';

import { checkReferralCode } from 'actions/user';
import { useLogin } from 'components/Auth/Login/state';
import { DEFAULT_REFERRAL_CODE } from 'components/Auth/Signup/constants';
import { oAuthSignup } from 'components/Auth/Signup/helpers';
import { OAUTH_PROVIDERS } from 'constants/enums';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import routes from 'constants/routes';
import { getDeviceOSTypeForAnalytics, getTrackierClickId } from 'helpers/analytics';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { useAppDispatch } from 'storeHooks';

const GoogleOneTapLogin = () => {
  const appDispatch = useAppDispatch();
  const history = useHistory();
  const { state: loginState, dispatch: loginDispatch } = useLogin();
  const [cookies] = useCookies(['referral_code']);
  const [referralCode, setReferralCode] = useState('');

  const setDefaultReferralCode = () => {
    setReferralCode(DEFAULT_REFERRAL_CODE);
  };

  // Use referralCode from cookies if it is valid
  useEffect(() => {
    if (cookies.referral_code && cookies.referral_code !== 'undefined') {
      appDispatch(checkReferralCode(cookies.referral_code))
        .then(() => {
          setReferralCode(cookies.referral_code);
        })
        .catch(() => {
          setDefaultReferralCode();
        });
    } else {
      setDefaultReferralCode();
    }
  }, [appDispatch, cookies.referral_code]);

  useGoogleOneTapLogin({
    onSuccess: response => {
      const jwtToken = response?.credential;

      if (!jwtToken) {
        trackMixpanelEvent(MIXPANEL_EVENT_NAMES.SIGN_UP.GOOGLE_ONE_TAP_FAILED, {
          reason: 'no_jwt',
        });
        return;
      }

      const decoded = jwtDecode(jwtToken);
      // One tap login/signup is only supported on Indian Exchange right now.
      const country = 'India';
      const userEmail = decoded?.email;
      const provider = OAUTH_PROVIDERS.GOOGLE;

      const bodyData = {
        oauth_token: jwtToken,
        country,
        signup_referral_code: referralCode,
        oauth: provider,
        tracking_info: {},
        device_os_type: getDeviceOSTypeForAnalytics(),
        trackier_click_id: getTrackierClickId(),
      };

      oAuthSignup({
        bodyData,
        userEmail,
        provider,
        country,
        referralCode,
        isOneTapLogin: true,
        history,
        appDispatch,
        loginDispatch,
      });
    },
    onError: () => {
      trackMixpanelEvent(MIXPANEL_EVENT_NAMES.SIGN_UP.GOOGLE_ONE_TAP_FAILED, {
        reason: 'error_callback',
      });
    },
    promptMomentNotification: notification => {
      if (notification.isDisplayed()) {
        trackMixpanelEvent(MIXPANEL_EVENT_NAMES.SIGN_UP.GOOGLE_ONE_TAP_SHOWN);
      }

      if (notification.isSkippedMoment()) {
        trackMixpanelEvent(MIXPANEL_EVENT_NAMES.SIGN_UP.GOOGLE_ONE_TAP_CLOSED, {
          closeReason: notification.getSkippedReason(),
        });
      }
    },
    disabled: !referralCode, // Disable one tap login until referralCode is available
  });

  useEffect(() => {
    // Navigate to login page for otp verification or for showing errors.
    if (loginState.pre2faToken || loginState.oAuth.error) {
      history.push({
        pathname: routes.login,
        state: { loginState, isOneTapLogin: true },
      });
    }
  }, [history, loginState]);
};

export default GoogleOneTapLogin;
